import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import '@xterm/xterm/css/xterm.css';
const TERMINAL_AUTH_URL = `${(process.env.REACT_APP_MOMENTUMCLAW_URL || 'https://momentumclawai-api.momentumamp.com')}/api/ams/terminal-auth`;
const SCREENSHOT_URL = `${(process.env.REACT_APP_MOMENTUMCLAW_URL || 'https://momentumclawai-api.momentumamp.com')}/api/ams/openclaw-screenshot`;

type TabId = 'vm' | 'terminal';
type TerminalStatus = 'connecting' | 'connected' | 'error' | 'disconnected';

interface OpenClawSidebarPanelProps {
  isConnected?: boolean;
}

const OpenClawSidebarPanel: React.FC<OpenClawSidebarPanelProps> = ({ isConnected = true }) => {
  const [activeTab, setActiveTab] = useState<TabId>('vm');
  const [clock, setClock] = useState(() =>
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  );

  useEffect(() => {
    const id = setInterval(() => {
      setClock(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // Disconnected state
  if (!isConnected) {
    return (
      <div className="openclaw-panel">
        <div className="openclaw-panel__status openclaw-panel__status--disconnected">
          <div className="openclaw-panel__status-dot openclaw-panel__status-dot--disconnected" />
          <span>Not Connected</span>
        </div>

        <div className="openclaw-panel__disconnected">
          <div className="openclaw-panel__disconnected-glow" />

          <motion.div
            className="openclaw-panel__disconnected-icon"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <div className="openclaw-panel__disconnected-icon-ring" />
            <span>🖥️</span>
          </motion.div>

          <motion.div
            className="openclaw-panel__disconnected-text"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.3 }}
          >
            <h3>VM Not Active</h3>
            <p>Request access to connect to a virtual machine and enable MomentumClaw automation.</p>
          </motion.div>

          <motion.div
            className="openclaw-panel__disconnected-features"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.3 }}
          >
            <div className="openclaw-panel__disconnected-feature">
              <span className="openclaw-panel__disconnected-feature-icon">⚡</span>
              <span>AI-powered extraction</span>
            </div>
            <div className="openclaw-panel__disconnected-feature">
              <span className="openclaw-panel__disconnected-feature-icon">🔒</span>
              <span>Secure &amp; compliant</span>
            </div>
            <div className="openclaw-panel__disconnected-feature">
              <span className="openclaw-panel__disconnected-feature-icon">🔄</span>
              <span>Auto-sync to AMS</span>
            </div>
          </motion.div>

          <motion.div
            className="openclaw-panel__disconnected-waiting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="openclaw-panel__disconnected-waiting-dots">
              <span /><span /><span />
            </div>
            <span>Awaiting access request</span>
          </motion.div>
        </div>

        <div className="openclaw-panel__footer openclaw-panel__footer--disconnected">
          <span className="openclaw-panel__footer-icon">💡</span>
          <span>Click "Request Access" to get started with MomentumClaw.</span>
        </div>
      </div>
    );
  }

  // Connected state — tabs: VM screenshot + Terminal
  return (
    <div className="openclaw-panel">
      {/* VM Screenshot tab */}
      <ScreenshotView clock={clock} />
    </div>
  );
};

// ─── Screenshot polling view ───────────────────────────────────────────────────

function ScreenshotView({ clock: _clock }: { clock: string }) {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let fetching = false;

    async function poll() {
      if (!mountedRef.current || fetching) return;
      fetching = true;
      try {
        const authToken = localStorage.getItem('authToken') ?? '';
        const res = await fetch(`${SCREENSHOT_URL}?t=${Date.now()}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok && res.headers.get('content-type')?.includes('image') && mountedRef.current) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          setScreenshot(prev => {
            if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
            return url;
          });
        }
      } catch { /* ignore */ }
      fetching = false;
    }

    poll();
    const id = setInterval(poll, 2000);
    return () => { mountedRef.current = false; clearInterval(id); };
  }, []);

  return (
    <>
      {/* Fullscreen overlay */}
      {expanded && screenshot && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)',
            display: 'flex', flexDirection: 'column',
          }}
          onClick={() => setExpanded(false)}
        >
          <div style={{
            height: 40, background: '#0a0b14', borderBottom: '1px solid #2a2b3d',
            display: 'flex', alignItems: 'center', padding: '0 16px', gap: 8, flexShrink: 0,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
            <span style={{ fontSize: 12, color: '#d1d5db', fontWeight: 500 }}>VM Browser</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>Click anywhere to close</span>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 8 }}>
            <img src={screenshot} alt="Live VM" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
          </div>
        </div>
      )}

      {/* Minimal card */}
      <div style={{
        margin: '8px', borderRadius: 12, border: '1px solid #2a2b3d',
        background: '#161726', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', borderBottom: '1px solid #2a2b3d',
        }}>
          {/* Monitor icon */}
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
          </svg>
          <span style={{ fontSize: 11, fontWeight: 500, color: '#9ca3af' }}>VM Browser</span>
          {/* Live dot */}
          <span style={{
            marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%',
            background: screenshot ? '#4ade80' : '#6b7280',
            display: 'inline-block',
            boxShadow: screenshot ? '0 0 6px #4ade80' : 'none',
          }} />
          {/* Expand button */}
          {screenshot && (
            <button
              onClick={() => setExpanded(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#6b7280', display: 'flex', alignItems: 'center' }}
              title="Expand"
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          )}
        </div>

        {/* Screenshot area */}
        <div style={{ position: 'relative', background: '#0d0e1a', minHeight: 120 }}>
          {!screenshot ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              height: 160, gap: 8, color: '#6b7280', fontSize: 12,
            }}>
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              <span>Waiting for VM screenshot…</span>
            </div>
          ) : (
            <img
              src={screenshot}
              alt="Live VM"
              style={{ width: '100%', height: 'auto', display: 'block', cursor: 'pointer' }}
              onClick={() => setExpanded(true)}
              title="Click to expand"
            />
          )}
        </div>
      </div>
    </>
  );
}

// ─── xterm Terminal view ───────────────────────────────────────────────────────

function TerminalView() {
  const termRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [termStatus, setTermStatus] = useState<TerminalStatus>('connecting');

  useEffect(() => {
    let mounted = true;

    async function init() {
      if (!termRef.current) return;

      try {
        const [{ Terminal }, { FitAddon }] = await Promise.all([
          import('@xterm/xterm'),
          import('@xterm/addon-fit'),
        ]);
        if (!mounted || !termRef.current) return;

        const authToken = localStorage.getItem('authToken') ?? '';
        const authRes = await fetch(TERMINAL_AUTH_URL, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (!authRes.ok) {
          if (mounted) setTermStatus('error');
          return;
        }

        const { wsUrl, wsUrlDirect, password } = await authRes.json();
        if (!mounted) return;

        const term = new Terminal({
          theme: {
            background: '#161726',
            foreground: '#e2e8f0',
            cursor: '#8b5cf6',
            cursorAccent: '#161726',
            selectionBackground: '#8b5cf620',
            black: '#1a1b26',
            red: '#f87171',
            green: '#4ade80',
            yellow: '#fbbf24',
            blue: '#60a5fa',
            magenta: '#c084fc',
            cyan: '#22d3ee',
            white: '#e2e8f0',
          },
          fontSize: 11,
          fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
          cursorBlink: true,
          scrollback: 1000,
          convertEol: true,
        });

        const fit = new FitAddon();
        term.loadAddon(fit);
        term.open(termRef.current);
        fit.fit();

        const termWsUrl = `${wsUrl}/terminal?token=${password}&cols=${term.cols}&rows=${term.rows}`;
        const termWsUrlFallback = `${wsUrlDirect}/terminal?token=${password}&cols=${term.cols}&rows=${term.rows}`;
        let ws = new WebSocket(termWsUrl);
        let usedFallback = false;

        const wireWs = (socket: WebSocket) => {
          socket.onopen = () => { if (mounted) setTermStatus('connected'); };
          socket.onmessage = (evt) => {
            try {
              const msg = JSON.parse(evt.data);
              if (msg.type === 'output') term.write(msg.data);
            } catch { /* skip non-JSON */ }
          };
          socket.onerror = () => { if (mounted) setTermStatus('error'); };
          socket.onclose = () => { if (mounted) setTermStatus('disconnected'); };
          term.onData((data: string) => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ type: 'input', data }));
            }
          });
          wsRef.current = socket;
        };

        ws.onerror = () => {
          if (!usedFallback) {
            usedFallback = true;
            ws.close();
            ws = new WebSocket(termWsUrlFallback);
            wireWs(ws);
          } else if (mounted) {
            setTermStatus('error');
          }
        };

        wireWs(ws);

        const ro = new ResizeObserver(() => {
          fit.fit();
          const s = wsRef.current;
          if (s?.readyState === WebSocket.OPEN) {
            s.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
          }
        });
        ro.observe(termRef.current!);

        const pingInterval = setInterval(() => {
          const s = wsRef.current;
          if (s?.readyState === WebSocket.OPEN) {
            s.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);

        return () => {
          clearInterval(pingInterval);
          ro.disconnect();
          ws.close();
          term.dispose();
        };
      } catch (err) {
        console.error('Terminal init error:', err);
        if (mounted) setTermStatus('error');
      }
    }

    const cleanupPromise = init();

    return () => {
      mounted = false;
      cleanupPromise?.then((cleanup) => cleanup?.());
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  return (
    <>
      <div className="openclaw-panel__status">
        <div className={`openclaw-panel__status-dot ${
          termStatus === 'connected' ? '' :
          termStatus === 'connecting' ? 'openclaw-panel__status-dot--connecting' :
          'openclaw-panel__status-dot--disconnected'
        }`} />
        <span>
          {termStatus === 'connected' ? 'Connected to VM' :
           termStatus === 'connecting' ? 'Connecting...' :
           termStatus === 'error' ? 'Connection Error' : 'Disconnected'}
        </span>
      </div>

      <div className="openclaw-panel__desktop">
        <div className="openclaw-panel__titlebar">
          <div className="openclaw-panel__titlebar-dots">
            <span className="openclaw-panel__dot openclaw-panel__dot--red" />
            <span className="openclaw-panel__dot openclaw-panel__dot--yellow" />
            <span className="openclaw-panel__dot openclaw-panel__dot--green" />
          </div>
          <span className="openclaw-panel__titlebar-text">Momentum Claw — Terminal</span>
        </div>

        <div className="openclaw-panel__content" style={{ padding: 0, position: 'relative' }}>
          {termStatus === 'error' && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', background: '#161726',
              color: '#f87171', fontSize: 12, gap: 8, zIndex: 10,
            }}>
              <span style={{ fontSize: 24 }}>⚠️</span>
              <span>Could not connect to VM terminal</span>
            </div>
          )}
          <div ref={termRef} style={{ width: '100%', height: '100%', minHeight: 0 }} />
        </div>
      </div>

      <div className="openclaw-panel__footer">
        <span className="openclaw-panel__footer-icon">🖥️</span>
        <span>Live VM terminal — type commands or let the agent control it.</span>
      </div>
    </>
  );
}

export default OpenClawSidebarPanel;

